import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import DatagridV2 from "ui-kit/src/Datagrid/DatagridV2";
import {
	teamGroupsColumns,
	teamGroupsFilters,
	teamGroupsBackendAdapter,
	type TeamGroupRow,
} from "../datagrid-configs/teamGroups";
import { ColumnVisibilityModal } from "../datagrid/ColumnVisibilityModal";
import { DatagridFilters } from "../datagrid/DatagridFilters";
import { useOperationsManagementTable } from "../operationsManagement/hooks/useOperationsManagementTable";
import { useTeamGroupFilterOptions } from "./hooks/useTeamGroupFormOptions";
import client from "libs/client";
import "../operationsManagement/OperationsManagementSummaryTableV3.css";

interface TeamGroupsTableProps {
	storageKey?: string;
}

export function TeamGroupsTable({ storageKey = "teamGroups" }: TeamGroupsTableProps) {
	const { dynamicOptions } = useTeamGroupFilterOptions();
	const navigate = useNavigate();
	const {
		pagination,
		setPagination,
		filterValues,
		filtersCollapsed,
		handleFilterChange,
		handleClearFilters,
		handleToggleFiltersCollapse,
		visibleColumns,
		defaultVisibility,
		columnOrder,
		defaultColumnOrder,
		showColumnModal,
		setShowColumnModal,
		handleVisibilityChange,
		groupColors,
		columnGroups,
		apiFilters,
		apiSort,
		simpleColumns,
		handleColumnWidthsChange,
	} = useOperationsManagementTable<TeamGroupRow>(
		{
			columns: teamGroupsColumns,
			filters: teamGroupsFilters,
			backendAdapter: teamGroupsBackendAdapter,
			storageKey,
			defaultPageSize: 50,
		},
		[],
	);

	const handleRowAction = useCallback(
		(row: TeamGroupRow, event: React.MouseEvent<HTMLTableRowElement>) => {
			const selectedText = window.getSelection();
			const hasSelectedRowText =
				selectedText !== null &&
				selectedText.toString().trim() !== "" &&
				selectedText.anchorNode !== null &&
				selectedText.focusNode !== null &&
				event.currentTarget.contains(selectedText.anchorNode) &&
				event.currentTarget.contains(selectedText.focusNode);

			if (hasSelectedRowText) {
				return;
			}

			navigate(`/team-group/${row.id}`);
		},
		[navigate],
	);

	const queryParams = useMemo(
		() => ({
			page: pagination.pageIndex + 1,
			limit: pagination.pageSize,
			filters: apiFilters.length > 0 ? apiFilters : undefined,
			order: apiSort ? { field: apiSort.field, direction: apiSort.direction as "ASC" | "DESC" } : undefined,
		}),
		[pagination.pageIndex, pagination.pageSize, apiFilters, apiSort],
	);

	const { data: teamGroupsResponse, isLoading, isError, refetch } = client.getTeamGroups.useQuery(queryParams, {
		retry: false,
	});

	const teamGroupsData = teamGroupsResponse?.data;
	const tableData = useMemo(
		() => (teamGroupsData?.items ?? []) as TeamGroupRow[],
		[teamGroupsData],
	);

	const pageCount = teamGroupsData?.pages ?? 0;
	const handleRetry = useCallback(() => refetch(), [refetch]);
	return (
		<div className="discover-card discover-card--for-table">
			<div className="operationsManagementSummaryTableV3">
				<div className={`operationsManagementSummaryTableV3-header ${filtersCollapsed ? "filters-collapsed" : ""}`}>
					{!filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerTop">
							<div className="operationsManagementSummaryTableV3-headerLeft">
								<DatagridFilters
									filters={teamGroupsFilters}
									filterValues={filterValues}
									onFilterChange={handleFilterChange}
									onClear={handleClearFilters}
									isCollapsed={filtersCollapsed}
									onToggleCollapse={handleToggleFiltersCollapse}
									dynamicOptions={dynamicOptions}
									columnVisibility={visibleColumns}
								/>
							</div>
						</div>
					)}

					{filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerSingleRow">
							<div className="operationsManagementSummaryTableV3-headerLeft">
								<DatagridFilters
									filters={teamGroupsFilters}
									filterValues={filterValues}
									onFilterChange={handleFilterChange}
									onClear={handleClearFilters}
									isCollapsed={filtersCollapsed}
									onToggleCollapse={handleToggleFiltersCollapse}
									dynamicOptions={dynamicOptions}
									columnVisibility={visibleColumns}
								/>
							</div>
							<div className="operationsManagementSummaryTableV3-headerRight">
								<button
									type="button"
									className="operationsManagementSummaryTableV3-columnBtn"
									onClick={() => setShowColumnModal(true)}
									title="Configure column visibility"
								>
									Column visibility
								</button>
							</div>
						</div>
					)}

					{!filtersCollapsed && (
						<div className="operationsManagementSummaryTableV3-headerBottom">
							<button
								type="button"
								className="operationsManagementSummaryTableV3-columnBtn"
								onClick={() => setShowColumnModal(true)}
								title="Configure column visibility"
							>
								Column visibility
							</button>
						</div>
					)}
				</div>

				<div className="operationsManagementSummaryTableV3-table">
					<DatagridV2<TeamGroupRow>
						noResults="No team groups found"
						data={tableData}
						simpleColumns={simpleColumns}
						pagination={pagination}
						setPagination={setPagination}
						pageCount={pageCount}
						isLoading={isLoading}
						isError={isError}
						onRetry={handleRetry}
						columnVisibility={visibleColumns}
						showPageSizeSelector
						totalItems={teamGroupsData?.total}
						onColumnWidthsChange={handleColumnWidthsChange}
						autoSizeColumns
						stickyHeader
						rowAction={handleRowAction}
					/>
				</div>

				{showColumnModal && (
					<ColumnVisibilityModal
						columnGroups={columnGroups}
						columnVisibility={visibleColumns}
						defaultVisibility={defaultVisibility}
						onVisibilityChange={handleVisibilityChange}
						onClose={() => setShowColumnModal(false)}
						columnOrder={columnOrder}
						defaultColumnOrder={defaultColumnOrder}
						customViews={[]}
						selectedCustomViewId={null}
						onCustomViewsChange={() => undefined}
						groupColors={groupColors}
					/>
				)}
			</div>
		</div>
	);
}

export default TeamGroupsTable;
